import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Chip } from 'src/components/shared/Chip'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { BusinessEntityVerification } from 'src/interfaces/documentReview'

interface Props {
  verifications: BusinessEntityVerification[]
}

/** Persona check statuses (para-node `PersonaVerificationCheckStatus`). */
const VERIFICATION_STATUS_STYLES: Record<string, { label: string; container: any; text: any }> = {
  passed: {
    label: 'Passed',
    container: tw`bg-success-200`,
    text: tw`text-success-500`,
  },
  failed: {
    label: 'Failed',
    container: tw`bg-critical-100`,
    text: tw`text-critical-500`,
  },
}

const BusinessEntityCardComponent = ({ verifications }: Props) => {
  return (
    <SectionCard title="Business entity verifications" dataTestId="document-review-business-card">
      <div css={tw`flex flex-col gap-2`}>
        {verifications.map((verification, index) => {
          const statusStyles = Object.prototype.hasOwnProperty.call(
            VERIFICATION_STATUS_STYLES,
            verification.status,
          )
            ? VERIFICATION_STATUS_STYLES[verification.status]
            : undefined

          return (
            <div key={`${verification.name}-${index}`} css={tw`flex flex-col`}>
              <div css={tw`flex flex-row items-center gap-2`}>
                <Typography variant="body2" css={tw`w-48 shrink-0 text-shades-500`}>
                  {verification.name}
                </Typography>
                {statusStyles ? (
                  <Chip
                    text={statusStyles.label}
                    className={statusStyles.container}
                    textClassName={statusStyles.text}
                    dataTestId={`business-entity-verification-status-${verification.status}`}
                  />
                ) : (
                  // An unknown status is shown verbatim rather than mislabelled as passed or failed.
                  <Chip text={verification.status} />
                )}
              </div>
              {verification.failedReason ? (
                <Typography variant="body2" css={tw`text-xs text-shades-400`}>
                  {verification.failedReason}
                </Typography>
              ) : null}
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}

export const BusinessEntityCard = memo(BusinessEntityCardComponent)
